import Button from "../../components/Button";
import Badge from "../../components/Badge";
import { buildMedicationQuantityLabel } from "../../utils/medication";

interface MedicationReviewRequestItem {
    id: number;
    name: string;
    quantity?: number;
    unit_display?: string | null;
    note?: string | null;
    processed_action?: string | null;
}

interface MedicationChangeRequest {
    id: number;
    prescription_number?: string | null;
    patient_name?: string | null;
    order_time_display?: string | null;
    medication_review_request_items?: MedicationReviewRequestItem[];
}

interface MedicationChangeRequestCardProps {
    requestChange: MedicationChangeRequest;
    status: "completed" | "pending";
    onProcessRequest: (requestChange: MedicationChangeRequest) => void;
}
/**
 * MedicationChangeRequestCard
 * 
 * A card component to display medication change request details.
 * 
 * @param {object} props
 * @param {object} props.requestChange
 *   The medication change request data.
 * @param {string} props.status
 *   The status of the request ("completed" or "pending").
 * @param {function} props.onProcessRequest
 *  Function to call when processing a request.
 */
export default function MedicationChangeRequestCard({
    requestChange,
    status,
    onProcessRequest,
}: MedicationChangeRequestCardProps) {

    function buildPrescriptionDetails() {
        const prescriptionDetails = [
            { label: "เลขที่ใบสั่งยา", value: requestChange.prescription_number },
            { label: "ผู้ป่วย", value: requestChange.patient_name },
            { label: "เวลาสั่ง", value: requestChange.order_time_display },
        ];

        return (
            <div className="flex flex-1 rounded-md px-5 py-3 gap-5 bg-main-background-system-default-dim items-start">
                {prescriptionDetails.map((detail) => (
                    <div key={detail.label} className={`flex items-start gap-3 ${detail.label !== "เลขที่ใบสั่งยา" ? "flex-1 max-w-[250px]" : ""}`}>
                        <div className="font-ui font-body text-body-base text-main-typography-system-description whitespace-nowrap">{detail.label}</div>
                        <div className="font-ui font-body text-body-base text-main-typography-system-default break-words flex-1" title={detail.value ?? undefined}>{detail.value || "-"}</div>
                    </div>
                ))}
            </div>
        );
    }

    function buildProcessButton() {
        return (
            <Button hierarchy="primary" size="md" text="จัดการคำร้อง" className="w-[150px]" onClick={() => onProcessRequest(requestChange)} />
        );
    }

    function buildPendingMedicationItems() {
        return (
            <div className="flex flex-col gap-3 pl-3">
                {(requestChange.medication_review_request_items ?? []).map((medication, index) => (
                    <div key={medication.id} className="flex flex-col gap-3">
                        <div className="flex flex-row justify-between items-center h-8">
                            <span className="font-ui font-header text-body-base text-main-typography-system-default">
                                {medication.name}
                            </span>
                            <div className="flex items-center gap-3">
                                <span className="font-ui font-body text-body-base text-main-typography-system-description w-[176px]">
                                    จำนวนที่สั่ง {buildMedicationQuantityLabel(medication.quantity, medication.unit_display) || "-"}
                                </span>
                                <span className="font-ui font-body text-body-base text-main-typography-system-description">ปัญหา</span>
                                <Badge text={medication.note || "-"} color="vivid" size="md" />
                            </div>
                        </div>
                        {index < (requestChange.medication_review_request_items ?? []).length - 1 && (
                            <hr className="border-t border-main-border-system-default w-full" />
                        )}
                    </div>
                ))}
            </div>
        );
    }

    function buildCompletedMedicationItems() {
        return (
            <div className="flex flex-col gap-3 pl-3">
                {(requestChange.medication_review_request_items ?? []).map((medication, index) => (
                    <div key={medication.id} className="flex flex-col gap-3">
                        <div className="flex flex-row justify-between items-center h-8">
                            <span className="font-ui font-header text-body-base text-main-typography-system-default">
                                {medication.name}
                            </span>
                            <div className="flex flex-row items-center gap-3">
                                <div className="flex flex-row items-center gap-3 w-[240px]">
                                    <span className="font-ui font-body text-body-base text-main-typography-system-description">ปัญหา</span>
                                    <span className="font-ui font-header text-body-base text-main-typography-system-default">{medication.note || "-"}</span>
                                </div>
                                <div className="flex flex-row items-center gap-3 w-[240px]">
                                    <span className="font-ui font-body text-body-base text-main-typography-system-description">การดำเนินการ</span>
                                    <span className="font-ui font-header text-body-base text-main-typography-system-default">{medication.processed_action || "-"}</span>
                                </div>
                            </div>
                        </div>
                        {index < (requestChange.medication_review_request_items ?? []).length - 1 && (
                            <hr className="border-t border-main-border-system-default w-full" />
                        )}
                    </div>
                ))}
            </div>
        );
    }


    return (
        <div className="flex flex-col rounded-md p-5 gap-5 bg-main-background-system-default border border-main-border-system-default" >
            <div className="flex gap-4">
                {buildPrescriptionDetails()}
                {status !== "completed" && (buildProcessButton())}
            </div>
            {status === "pending" && (buildPendingMedicationItems())}
            {status === "completed" && (buildCompletedMedicationItems())}
        </div >
    );
}
